import React, {FC, useEffect, useMemo, useState} from "react";
import {javascript} from "@codemirror/lang-javascript";
import CodeMirror from "@uiw/react-codemirror";
import {eclipse} from "@uiw/codemirror-theme-eclipse";
import {EditorView} from '@codemirror/view';
import {json} from "@codemirror/lang-json";
import useCommon from "../../../common/hooks/useCommon";
import {Link, useLocation, useParams} from "react-router-dom";
import {addressService} from "../../../store/actions";
import BCopy from "../../../components/b-copy";
import {useTranslation} from "react-i18next";
import {Dropdown} from "react-bootstrap";
import BTooltip from "../../../components/b-tooltip";
import moment from "moment";
import {useSelector} from "react-redux";
import TokenImage from "../token-image";

interface DetailsData {
    name: any;
    is_verified: any;
    compiler_version: any;
    verified_at: any;
    optimization_enabled: any;
    optimization_runs: any;
    source_code: any;
    abi: any;
    constructor_args: any;
    creation_bytecode: any;
    deployed_bytecode: any;
    additional_sources: any;
    evm_version: any;
    file_path: any;
    compiler_settings: {
        optimizer: {
            enabled: boolean;
            runs: number;
        }
    };
    language: string;
    license_type: string;
    decoded_constructor_args: any;
}

interface ReadCodeProps {
    isActive: boolean;
}

const ReadCode: FC<ReadCodeProps> = () => {
    const {getXDC} = useCommon();
    const {address, token} = useParams();
    const {t, i18n} = useTranslation();
    const stats = useSelector((e: Object) => (e as any)?.common?.stats);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const location = useLocation();
    const hashSplit = location.hash.split("#F");
    const hashIndex = isNaN(Number(hashSplit?.[1])) ? -1 : Number(hashSplit?.[1]);
    const [details, setDetails] = useState<DetailsData>({
        name: "",
        is_verified: true,
        compiler_version: "",
        verified_at: "",
        optimization_enabled: "",
        optimization_runs: false,
        source_code: "",
        abi: [],
        constructor_args: "",
        creation_bytecode: "",
        deployed_bytecode: "",
        additional_sources: [],
        evm_version: "",
        file_path: "",
        compiler_settings: {
            optimizer: {
                enabled: false,
                runs: 0
            }
        },
        language: "",
        license_type: "",
        decoded_constructor_args: [],
    });
    const decodedView = useMemo(() => {
        if (!details?.decoded_constructor_args?.length) return ''
        const str = details?.decoded_constructor_args.map((item: any, index: number) => {
            let value = item[0];
            let details = item[1];
            let name = details.name;
            let type = details.type;
            return `Arg [${index}] : ${name} (${type}): ${type === "address" ? value.toUpperCase() : value}`;
        }).join('\n')
        return `\n\n-----Decoded View---------------\n${str}`
    }, [details?.decoded_constructor_args])

    const sourceCode = useMemo(() => {
        if (!details?.verified_at) return ''

        return `/**\n*Submitted for verification at OpenScan.ai on ${moment(details?.verified_at).format('YYYY-MM-DD')}\n*/\n\n// File: ${details?.file_path}\n\n`
    }, [details?.decoded_constructor_args])

    const [loading, setLoading] = useState(true);

    const getPrice = async () => {
        try {
            setLoading(true);
            const response = await addressService.getVerifiedAddress(address || token, "");
            setDetails(response);
            setLoading(false);
        } catch (err) {
            console.log(err);
            setLoading(false);
        }
    };

    useEffect(() => {
        getPrice();
    }, [address, token]);

    useEffect(() => {
        if (hashIndex) {
            setTimeout(() => {
                document.getElementById(`F${hashIndex}`)?.scrollIntoView({behavior: 'smooth', inline: "start"});
            }, 1000)
        }
    }, [hashIndex]);
    const toggleFullscreen = () => {
        setIsFullscreen(!isFullscreen);
    };

    return (
        <div className="py-3">
            <div className="row">
                <div className="col-lg-12">
                    {details?.is_verified ? (
                        <h3 className="h6 text-dark fw-bold pb-2 mb-4">
                            <span>
                                <i className="fa fa-check-circle bs_verified-contract me-2"></i>
                                <strong>{t("Contract Source Code Verified")}</strong>
                            </span>
                        </h3>
                    ) : (
                        <div>
                            <h3 className="mb-2">
                                {t("Are you the contract creator")}? &nbsp;
                                <Link to={`/contracts/verify?contract=${address || token}`}>
                                    {t("Verify And Publish")}
                                </Link>
                                &nbsp;{t("your contract source code today")}!
                            </h3>
                            <div style={{maxWidth: "100%", overflow: "auto"}}>
                                <pre>
                                    <CodeMirror
                                        value={(details?.deployed_bytecode) || ""}
                                        theme={eclipse}
                                        height="300px"
                                        extensions={[EditorView.lineWrapping]}
                                        basicSetup={{
                                            lineNumbers: false,
                                            highlightActiveLine: false,
                                        }}
                                        // onChange={onChange}
                                    />
                                </pre>
                            </div>
                        </div>
                    )}
                </div>
                {details?.is_verified && (
                    <React.Fragment>
                        <div className="col-lg-6">
                            <div className="bs-list">
                                <div className="bs-list-row">
                                    <div className="bs-list-title">{t("Contract Name")}:</div>
                                    <div className="bs-list-content"><span className="h6 fw-bold mb-0">{details?.name}</span></div>
                                </div>
                                <hr/>
                                <div className="bs-list-row">
                                    <div className="bs-list-title">{t("Compiler Version")}:</div>
                                    <div className="bs-list-content">
                                        <span className="h6 fw-bold mb-0"> {details?.compiler_version} </span>
                                    </div>
                                </div>
                                {details?.optimization_enabled && (
                                    <React.Fragment>
                                        <div className="bs-list-row">
                                            <div className="bs-list-title">
                                                {t("Optimization Runs")}:
                                            </div>
                                            <div className="bs-list-content">
                                                {details?.optimization_runs}
                                            </div>
                                        </div>
                                        <hr/>
                                    </React.Fragment>
                                )}
                                <hr className="d-md-none"/>
                            </div>
                        </div>
                        <div className="col-lg-6">
                            <div className="bs-list">
                                <div className="bs-list-row">
                                    <div className="bs-list-title">
                                        {t("Optimization Enabled")}:
                                    </div>
                                    <div className="bs-list-content">
                                        <span className="h6 fw-bold mb-0">
                                            {details?.optimization_enabled ? "Yes" : "No"}
                                            {" "} <span className="fw-normal">with </span> {details?.optimization_runs ?? details?.compiler_settings?.optimizer?.runs ?? 0}
                                            <span className="fw-normal"> runs </span>
                                        </span>
                                    </div>
                                </div>
                                <hr/>
                                <div className="bs-list-row">
                                    <div className="bs-list-title">{t("Other Settings")}:</div>
                                    <div className="bs-list-content"><span className="h6 fw-bold mb-0">{details?.evm_version} {" "}
                                        <span className="fw-normal">{t("Evm Version,")}</span> <b>{details?.license_type.toUpperCase()}</b>
                                        <BTooltip text={t("Click to learn more, open source license")}>
                                            <a className="fw-normal ms-1" href='/contract-license-types'>license</a>
                                        </BTooltip>
                                    </span></div>
                                </div>
                                <hr className="d-md-none"/>
                            </div>
                        </div>
                        <div className="col-lg-12">
                            <div className="bs-list">
                                {details?.additional_sources?.length === 0 ? (
                                    <React.Fragment>
                                        <div className="bs-list-row">
                                            <div className="bs-list-title">
                                                <i className="bi bi-file-earmark-code"></i> {" "}
                                                {t("Contract Source Code")} {`(${details?.language})` || "Verified"}:
                                            </div>
                                            <div className="bs-list-content">
                                                <div className="d-flex align-items-center justify-content-end">
                                                    <Dropdown className="d-flex bs_token-social btn-sm btn-icon btn-pro me-1">
                                                        <Dropdown.Toggle id="dropdown-button-dark-example1" variant="white" size="sm">
                                                            IDE
                                                        </Dropdown.Toggle>
                                                        <Dropdown.Menu variant="dark">
                                                            <Dropdown.Item href={`https://vscode.openscan.ai/ethereum/${address || token}`} className="bs-small-font"
                                                                           target="_blank">
                                                                <TokenImage image={stats?.coin_image} symbol={process.env.REACT_APP_BASE_UNIT}
                                                                            params={{
                                                                                width: 16,
                                                                                height: 16,
                                                                                className: "bs-icon-avatar mr-2"
                                                                            }}/> {process.env.REACT_APP_BASE_EXPLORER || "OpenScan"} IDE
                                                            </Dropdown.Item>
                                                            <Dropdown.Item href="#" className="bs-small-font" rel="nofollow">
                                                                🤖 Code Reader
                                                            </Dropdown.Item>
                                                            <Dropdown.Item href="#" className="bs-small-font" rel="nofollow">
                                                                Remix IDE
                                                            </Dropdown.Item>
                                                        </Dropdown.Menu>
                                                    </Dropdown>
                                                    <Dropdown className="d-flex bs_token-social btn-sm btn-icon btn-pro me-1">
                                                        <Dropdown.Toggle id="dropdown-button-dark-example1" variant="white" size="sm">
                                                            More Options
                                                        </Dropdown.Toggle>
                                                        <Dropdown.Menu variant="dark">
                                                            <Dropdown.Item href="#" className="bs-small-font" target="_blank">
                                                                todo: <i className="bi bi-file-earmark-text me-1"></i> Similar
                                                            </Dropdown.Item>
                                                            <Dropdown.Item href="#" className="bs-small-font" rel="nofollow">
                                                                todo: Sol2Uml
                                                            </Dropdown.Item>
                                                            <Dropdown.Item href="#" className="bs-small-font" rel="nofollow">
                                                                todo: <i className="bi bi-file-earmark-text me-1"></i>Submit Audit
                                                            </Dropdown.Item>
                                                            <Dropdown.Item href="#" className="bs-small-font" rel="nofollow">
                                                                todo: <i className="bi bi-file-earmark-text me-1"></i>Compare
                                                            </Dropdown.Item>
                                                        </Dropdown.Menu>
                                                    </Dropdown>
                                                </div>
                                            </div>
                                        </div>
                                        <div id="F1" className={`bs-list-row`}>
                                            <div className="bs-list-title"></div>
                                            <div className="bs-list-content">
                                                <div className="d-flex align-items-center justify-content-end">
                                                    <span>
                                                        <BCopy
                                                            value={details?.source_code}
                                                            tooltip={`${t("to clipboard")}`}
                                                        />
                                                        <BTooltip text={"Toggle Fullscreen"}>
                                                            <button onClick={toggleFullscreen} className="btn btn-sm btn-icon btn-pro me-1" style={{marginLeft: "5px"}}>
                                                                {isFullscreen ? <i className="bi bi-fullscreen-exit"></i> : <i className="bi bi-fullscreen"></i>}
                                                            </button>
                                                        </BTooltip>
                                                        <span className="h_link">
                                                            <BCopy
                                                                value={window.location.origin + window.location.pathname + hashSplit?.[0] + `#F1`}
                                                                icon="bi-link"
                                                                tooltip={`${t("Permalink")}`}
                                                            />
                                                        </span></span>
                                                </div>
                                                <CodeMirror
                                                    value={sourceCode + details?.source_code}
                                                    theme={eclipse}
                                                    height={isFullscreen ? "100vh" : "500px"}
                                                    extensions={[javascript({jsx: true}), EditorView.lineWrapping]}
                                                    basicSetup={{
                                                        lineNumbers: true,
                                                        highlightActiveLine: false,
                                                    }}
                                                    // onChange={onChange}
                                                />
                                            </div>
                                        </div>
                                        <hr/>
                                    </React.Fragment>
                                ) : (details?.additional_sources?.map((e: any, i: number) => {
                                        return (
                                            <React.Fragment key={i}>
                                                <div id={`F${i + 1}`} className={`bs-list-row`}>
                                                    <div className="bs-list-title">
                                                        {t("File")} {i + 1} {t("of")}{" "}
                                                        {details?.additional_sources?.length}:
                                                    </div>
                                                    <div className="bs-list-content">
                                                        <div className="d-flex justify-content-between align-items-center mb-2" style={{width: '100%'}}>
                                                            <div className="bs-list-title"
                                                                 style={{flexGrow: 1, marginRight: '10px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                                                {e?.file_path}
                                                            </div>
                                                            <div className="d-flex align-items-center">
                                                                <BCopy
                                                                    value={e?.source_code}
                                                                    tooltip={`${t("Copy source code to clipboard")}`}
                                                                />
                                                                <BTooltip text={"Toggle Fullscreen"}>
                                                                    <button onClick={toggleFullscreen} className="btn btn-sm btn-icon btn-pro me-1" style={{marginLeft: "5px"}}>
                                                                        {isFullscreen ? <i className="bi bi-fullscreen-exit"></i> : <i className="bi bi-fullscreen"></i>}
                                                                    </button>
                                                                </BTooltip>
                                                                <span className="h_link">
                                                                    <BCopy
                                                                        value={window.location.origin + window.location.pathname + `#code#F${i + 1}`} icon="bi-link"
                                                                        tooltip={`${t("Copy permalink to clipboard")}`}
                                                                    />
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <CodeMirror
                                                            value={e?.source_code}
                                                            theme={eclipse}
                                                            height={isFullscreen ? "100vh" : "500px"}
                                                            extensions={[javascript({jsx: true}), EditorView.lineWrapping]}
                                                            basicSetup={{
                                                                lineNumbers: true,
                                                                highlightActiveLine: false,
                                                            }}
                                                            // onChange={onChange}
                                                        />
                                                    </div>
                                                </div>
                                                <hr/>
                                            </React.Fragment>
                                        );
                                    }
                                ))}
                                <div className="bs-font mb-4">
                                    <div className="bs-font-secondary d-md-flex justify-content-between align-items-center py-2">
                                        <div className="bs-list-title">
                                            <i className="bi bi-file-earmark-text"></i> {t("Contract Security Audit")}
                                        </div>
                                    </div>
                                    <ul className="ps-4" style={{listStyle: 'disc'}}>
                                        <li>
                                            <span className="bg-badge text-dark fw-normal fs-sm py-1.5 me-1 px-2 cm-editor code-bg">
                                                <i className="bi bi-x-circle me-2"></i>
                                                {t("No Contract Security Audit Submitted")}
                                            </span>
                                            - &nbsp;
                                            <Link to={`/contactus?a=${address || token}`}>
                                                {t("Submit Audit Here")}
                                            </Link>
                                        </li>
                                    </ul>
                                </div>
                                <hr/>
                                <div className="bs-list-row">
                                    <div className="bs-list-title"><i className="bi bi-list-check"></i> ABI:</div>
                                    <div className="bs-list-content">
                                        <div className="d-flex align-items-center justify-content-end mb-2">
                                            <span className="d-flex align-items-center gap-1 mb-2 mb-lg-0">
                                                <Dropdown className="d-flex bs_token-social btn-sm btn-icon btn-pro me-1">
                                                    <Dropdown.Toggle id="dropdown-button-dark-example1" variant="white" size="sm">
                                                        Export ABI
                                                    </Dropdown.Toggle>
                                                    <Dropdown.Menu variant="dark">
                                                        <Dropdown.Item
                                                            href="https://api-xdc.blocksscan.io/api?module=contract&action=getabi&address=0x5eb7a07347f5ed3a7aa56b4ddf633cb4a107aa3a"
                                                            className="bs-small-font" target="_blank">
                                                            JSON Format
                                                        </Dropdown.Item>
                                                        <Dropdown.Item
                                                            href="https://api-xdc.blocksscan.io/api?module=contract&action=getabi&address=0x5eb7a07347f5ed3a7aa56b4ddf633cb4a107aa3a&format=raw"
                                                            className="bs-small-font" rel="nofollow">
                                                            RAW/Text Format
                                                        </Dropdown.Item>
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                                <BCopy
                                                    value={JSON.stringify(details?.abi, null, 2)}
                                                    tooltip={`${t("Copy ABI to clipboard")}`}
                                                />
                                                <BTooltip text={"Toggle Fullscreen"}>
                                                    <button
                                                        onClick={toggleFullscreen}
                                                        className="btn btn-sm btn-icon btn-pro me-1"
                                                        style={{marginLeft: "5px"}}
                                                    >
                                                        {isFullscreen ? <i className="bi bi-fullscreen-exit"></i> : <i className="bi bi-fullscreen"></i>}
                                                    </button>
                                                </BTooltip>
                                            </span>
                                        </div>
                                        <CodeMirror
                                            value={JSON.stringify(details?.abi, null, 2)}
                                            theme={eclipse}
                                            height={isFullscreen ? "100vh" : "200px"}
                                            extensions={[json()]}
                                            basicSetup={{
                                                lineNumbers: false,
                                                highlightActiveLine: false,
                                            }}
                                            // onChange={onChange}
                                        />
                                    </div>
                                </div>
                                <hr/>
                                <div className="bs-list-row">
                                    <div className="bs-list-title">
                                        <i className="bi bi-diagram-3"></i> {t("Constructor Arguments")} ({t("ABI-encoded")}):
                                    </div>
                                    <div className="bs-list-content">
                                        <CodeMirror
                                            value={getXDC(details?.constructor_args ?? "") + decodedView}
                                            theme={eclipse}
                                            height="200px"
                                            basicSetup={{
                                                lineNumbers: false,
                                                highlightActiveLine: false,
                                            }}
                                            extensions={[EditorView.lineWrapping]}
                                            // onChange={onChange}
                                        />
                                    </div>
                                </div>
                                <hr/>
                                <div className="bs-list-row">
                                    <div className="bs-list-title"><i className="bi bi-code-slash"></i> {t("Byte Code")}:</div>
                                    <div className="bs-list-content word-wrap">
                                        <CodeMirror
                                            value={getXDC(details?.creation_bytecode) || ""}
                                            theme={eclipse}
                                            height="100px"
                                            basicSetup={{

                                                lineNumbers: false,
                                                highlightActiveLine: false,
                                            }}
                                            extensions={[EditorView.lineWrapping]}
                                            // onChange={onChange}
                                        />
                                    </div>
                                </div>
                                <hr/>
                            </div>
                        </div>
                    </React.Fragment>
                )}
            </div>
        </div>
    );
};

export default ReadCode;
